import axios from 'axios';
import { API_BASE } from './constants';

// ─── Fuel Inventory API (existants) ──────────────────────────────────────────

export const fetchFuelData = async () => {
  const [invRes, txRes, todayRes, acRes, owRes, stRes] = await Promise.all([
    axios.get(`${API_BASE}/fuel/inventory`),
    axios.get(`${API_BASE}/fuel/transactions?limit=50`),
    axios.get(`${API_BASE}/fuel/today`),
    axios.get(`${API_BASE}/aircraft/`),
    axios.get(`${API_BASE}/owners/`),
    axios.get(`${API_BASE}/staff/`),
  ]);
  
  return {
    inventory: invRes.data,
    transactions: txRes.data,
    todayStats: todayRes.data,
    aircraft: acRes.data,
    owners: owRes.data,
    staff: stRes.data,
  };
};

export const saveTank = async (tank, editingTank) => {
  const payload = {
    ...tank,
    capacity_gallons:       tank.capacity_gallons      ? parseFloat(tank.capacity_gallons)      : undefined,
    current_volume_gallons: parseFloat(tank.current_volume_gallons),
    reorder_level_gallons:  tank.reorder_level_gallons ? parseFloat(tank.reorder_level_gallons) : undefined,
    price_per_gallon:       parseFloat(tank.price_per_gallon),
    tank_name:              tank.tank_name || undefined,
  };
  
  if (editingTank) {
    await axios.put(`${API_BASE}/fuel/inventory/${editingTank.id}`, payload);
  } else {
    await axios.post(`${API_BASE}/fuel/inventory`, payload);
  }
};

export const deleteTank = async (id) => {
  await axios.delete(`${API_BASE}/fuel/inventory/${id}`);
};

export const saveTransaction = async (tx, editingTx) => {
  const payload = {
    ...tx,
    gallons:          parseFloat(tx.gallons),
    price_per_gallon: parseFloat(tx.price_per_gallon),
    staff_id:         tx.staff_id || undefined,
    owner_id:         tx.owner_id || undefined,
  };
  
  if (editingTx) {
    await axios.put(`${API_BASE}/fuel/transactions/${editingTx.id}`, payload);
  } else {
    await axios.post(`${API_BASE}/fuel/transaction`, payload);
  }
};

export const deleteTransaction = async (id) => {
  await axios.delete(`${API_BASE}/fuel/transactions/${id}`);
};

export const saveDelivery = async (delivery) => {
  await axios.post(`${API_BASE}/fuel/delivery`, {
    ...delivery,
    gallons:          parseFloat(delivery.gallons),
    price_per_gallon: parseFloat(delivery.price_per_gallon),
  });
};

export const updatePrice = async (fuelType, price) => {
  await axios.put(`${API_BASE}/fuel/inventory/${fuelType}/price`, {
    price_per_gallon: parseFloat(price),
  });
};

// ─── Fuel Trucks API (corrigé avec slashes) ─────────────────────────────────────────

export const fetchTrucks = async (activeOnly = true) => {
  const response = await axios.get(`${API_BASE}/fuel/trucks/`, {
    params: { active_only: activeOnly }
  });
  return response.data;
};

export const fetchTruckById = async (truckId) => {
  const response = await axios.get(`${API_BASE}/fuel/trucks/${truckId}/`);
  return response.data;
};

export const saveTruck = async (truck, editingTruck) => {
  // Nettoyer et préparer le payload
  const payload = {
    truck_number: truck.truck_number,
    truck_name: truck.truck_name || null,
    fuel_type: truck.fuel_type,
    capacity_gallons: parseFloat(truck.capacity_gallons),
    current_volume_gallons: truck.current_volume_gallons ? parseFloat(truck.current_volume_gallons) : 0,
    license_plate: truck.license_plate || null,
    driver_name: truck.driver_name || null,
    driver_phone: truck.driver_phone || null,
    status: truck.status || 'available',
    last_maintenance_date: truck.last_maintenance_date || null,
    next_maintenance_date: truck.next_maintenance_date || null,
    notes: truck.notes || null,
    is_active: truck.is_active !== undefined ? truck.is_active : true,
  };
  
  // Nettoyer les valeurs null/undefined/vides
  Object.keys(payload).forEach(key => {
    if (payload[key] === undefined || payload[key] === '') {
      payload[key] = null;
    }
  });
  
  if (editingTruck) {
    const response = await axios.put(`${API_BASE}/fuel/trucks/${editingTruck.id}/`, payload);
    return response.data;
  } else {
    const response = await axios.post(`${API_BASE}/fuel/trucks/`, payload);
    return response.data;
  }
};

export const deleteTruck = async (id, hardDelete = false) => {
  const response = await axios.delete(`${API_BASE}/fuel/trucks/${id}/`, {
    params: { hard_delete: hardDelete }
  });
  return response.data;
};

export const refillTruck = async (truckId, refillData) => {
  const payload = {
    gallons: parseFloat(refillData.gallons),
    source_tank_type: refillData.source_tank_type,
    truck_meter_before: refillData.truck_meter_before ? parseFloat(refillData.truck_meter_before) : null,
    truck_meter_after: refillData.truck_meter_after ? parseFloat(refillData.truck_meter_after) : null,
    staff_id: refillData.staff_id || null,
    notes: refillData.notes || null,
  };
  
  // Nettoyer les valeurs null
  Object.keys(payload).forEach(key => {
    if (payload[key] === undefined || payload[key] === '') {
      payload[key] = null;
    }
  });
  
  const response = await axios.post(`${API_BASE}/fuel/trucks/${truckId}/refill/`, payload);
  return response.data;
};

export const getTruckTransactions = async (truckId, limit = 50) => {
  const response = await axios.get(`${API_BASE}/fuel/trucks/${truckId}/transactions/`, {
    params: { limit }
  });
  return response.data;
};

export const getTruckRefills = async (truckId, limit = 50) => {
  const response = await axios.get(`${API_BASE}/fuel/trucks/${truckId}/refills/`, {
    params: { limit }
  });
  return response.data;
};

export const getTruckStatus = async (truckId) => {
  const response = await axios.get(`${API_BASE}/fuel/trucks/${truckId}/status/`);
  return response.data;
};

export const createTruckTransaction = async (truckId, transaction) => {
  const payload = {
    aircraft_id: transaction.aircraft_id,
    owner_id: transaction.owner_id || null,
    gallons: parseFloat(transaction.gallons),
    fuel_type: transaction.fuel_type,
    price_per_gallon: parseFloat(transaction.price_per_gallon),
    truck_meter_start: transaction.truck_meter_start ? parseFloat(transaction.truck_meter_start) : null,
    truck_meter_end: transaction.truck_meter_end ? parseFloat(transaction.truck_meter_end) : null,
    staff_id: transaction.staff_id || null,
    notes: transaction.notes || null,
  };
  
  // Nettoyer les valeurs null
  Object.keys(payload).forEach(key => {
    if (payload[key] === undefined || payload[key] === '') {
      payload[key] = null;
    }
  });
  
  const response = await axios.post(`${API_BASE}/fuel/trucks/${truckId}/transaction/`, payload);
  return response.data;
};